import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

/**
 * Service for validating email domains by checking MX records
 * Uses DNS-over-HTTPS (DoH) to query MX records from the browser
 */
@Injectable({
  providedIn: 'root',
})
export class MxRecordService {
  // Google's public DNS-over-HTTPS endpoint
  private readonly dohEndpoint = 'https://dns.google/resolve';

  // Cache for MX record results to avoid repeated queries
  private readonly mxCache = new Map<string, boolean>();

  // Timeout for DNS queries (in milliseconds)
  private readonly queryTimeout = 5000;

  /**
   * Validates if a domain has valid MX records
   * @param domain - The domain to check (e.g., 'company.com')
   * @returns Observable<boolean> - true if domain has MX records, false otherwise
   */
  public validateMxRecord(domain: string): Observable<boolean> {
    if (!domain || typeof domain !== 'string') {
      return of(false);
    }

    const normalizedDomain = domain.toLowerCase().trim();

    // Check cache first
    if (this.mxCache.has(normalizedDomain)) {
      return of(this.mxCache.get(normalizedDomain) || false);
    }

    // Skip validation for known personal/academic email domains (they have MX records but we don't want them)
    // isKnownPersonalDomain() internally checks both personal providers AND academic domains
    if (this.isKnownPersonalDomain(normalizedDomain)) {
      this.mxCache.set(normalizedDomain, false);
      return of(false);
    }

    return this.queryMxRecord(normalizedDomain).pipe(
      timeout(this.queryTimeout),
      map((hasMx) => {
        // Cache the result
        this.mxCache.set(normalizedDomain, hasMx);
        return hasMx;
      }),
      catchError((error) => {
        console.warn(`MX record validation failed for domain ${normalizedDomain}:`, error);
        // On error, assume domain is valid to avoid blocking legitimate users
        // This is a fallback strategy - better to allow false positives than false negatives
        this.mxCache.set(normalizedDomain, true);
        return of(true);
      }),
    );
  }

  /**
   * Queries MX records for a domain using DNS-over-HTTPS
   * @param domain - The domain to query
   * @returns Observable<boolean> - true if MX records exist
   */
  private queryMxRecord(domain: string): Observable<boolean> {
    const url = `${this.dohEndpoint}?name=${encodeURIComponent(domain)}&type=MX`;

    return new Observable<boolean>((observer) => {
      fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/dns-json',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`DNS query failed: ${response.status} ${response.statusText}`);
          }
          return response.json();
        })
        .then((data) => {
          // Check if the response contains MX records
          const hasMxRecords = data.Answer && Array.isArray(data.Answer) && data.Answer.some((record: any) => record.type === 15);
          // MX record type is 15
          observer.next(hasMxRecords);
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  /**
   * Checks if a domain is a known personal email provider or academic/educational domain
   * Uses multiple detection methods:
   * 1. Exact match against known personal domains
   * 2. Wildcard pattern matching
   * 3. Academic domain TLD detection (comprehensive list)
   * 4. Academic keyword detection in domain name
   *
   * @param domain - The domain to check
   * @returns boolean - true if it's a known personal or academic domain
   */
  private isKnownPersonalDomain(domain: string): boolean {
    const personalDomains = [
      'gmail.com',
      'googlemail.com',
      'outlook.com',
      'hotmail.com',
      'live.com',
      'yahoo.com',
      'yahoo.co.uk',
      'yahoo.ca',
      'icloud.com',
      'me.com',
      'aol.com',
      'protonmail.com',
      'tutanota.com',
      'gmx.com',
      'mail.ru',
      'yandex.com',
      'qq.com',
      '163.com',
      '126.com',
      'sina.com',
      '*.edu', // Wildcard: matches any subdomain ending with .edu (e.g., abc.edu, mit.edu)
      '*.gov', // Wildcard: matches any subdomain ending with .gov
      'ucm.es',
      'gavrom.com',
      'huhao',
      'freeqiye.com',
      // Add more as needed
    ];

    // Check for exact match first
    if (personalDomains.includes(domain)) {
      return true;
    }

    // Check for wildcard patterns (*.gov, etc.)
    for (const pattern of personalDomains) {
      if (pattern.startsWith('*.')) {
        // Extract the suffix (e.g., '.gov' from '*.gov')
        const suffix = pattern.substring(1); // Remove the '*'
        // Check if domain ends with the suffix
        if (domain.endsWith(suffix)) {
          return true;
        }
      }
    }

    // Check if it's an academic/educational domain using comprehensive detection
    if (this.isAcademicDomain(domain)) {
      return true;
    }

    return false;
  }

  /**
   * Comprehensive academic/educational domain detection
   * Uses multiple methods to identify academic institutions:
   * 1. Academic TLDs (Top-Level Domains)
   * 2. Academic second-level domains
   * 3. Academic keywords in domain name
   *
   * @param domain - The domain to check
   * @returns boolean - true if it's an academic/educational domain
   */
  private isAcademicDomain(domain: string): boolean {
    // Method 1: Check for academic TLDs (most reliable)
    const academicTLDs = [
      '.edu', // United States educational institutions
      '.edu.au', // Australia
      '.edu.cn', // China
      '.edu.hk', // Hong Kong
      '.edu.tw', // Taiwan
      '.edu.sg', // Singapore
      '.edu.my', // Malaysia
      '.edu.ph', // Philippines
      '.edu.in', // India
      '.edu.pk', // Pakistan
      '.edu.bd', // Bangladesh
      '.edu.np', // Nepal
      '.edu.lk', // Sri Lanka
      '.edu.vn', // Vietnam
      '.edu.th', // Thailand
      '.edu.id', // Indonesia
      '.edu.kr', // South Korea
      '.edu.jp', // Japan
      '.edu.tr', // Turkey
      '.edu.sa', // Saudi Arabia
      '.edu.ae', // UAE
      '.edu.eg', // Egypt
      '.edu.za', // South Africa
      '.edu.ng', // Nigeria
      '.edu.ke', // Kenya
      '.edu.br', // Brazil
      '.edu.mx', // Mexico
      '.edu.ar', // Argentina
      '.edu.co', // Colombia
      '.edu.pe', // Peru
      '.edu.cl', // Chile
      '.ac.uk', // United Kingdom
      '.ac.nz', // New Zealand
      '.ac.za', // South Africa
      '.ac.jp', // Japan
      '.ac.kr', // South Korea
      '.ac.cn', // China
      '.ac.in', // India
      '.ac.th', // Thailand
      '.ac.id', // Indonesia
      '.ac.il', // Israel
      '.ac.at', // Austria
      '.ac.be', // Belgium
      '.ac.ir', // Iran
      '.ac.ma', // Morocco
      '.ac.ug', // Uganda
      '.ac.ke', // Kenya
      '.ac.tz', // Tanzania
      '.ac.rw', // Rwanda
      '.uni-', // German universities (e.g., uni-heidelberg.de)
      '.univ-', // French universities (e.g., univ-paris.fr)
      '.university.', // Generic university domains
      '.ac.no', // Norway academic
      '.edu.no', // Norway education
    ];

    // Check if domain ends with any academic TLD
    for (const tld of academicTLDs) {
      if (domain.endsWith(tld) || domain.includes(tld)) {
        return true;
      }
    }

    // Method 2: Check for academic second-level domains
    const academicSecondLevelDomains = [
      '.k12.', // K-12 schools (US)
      '.sch.', // Schools (various countries)
      '.school.', // Schools
      '.college.', // Colleges
      '.university.', // Universities
    ];

    for (const sld of academicSecondLevelDomains) {
      if (domain.includes(sld)) {
        return true;
      }
    }

    // Method 3: Check for known university domains (Nordic countries and others that don't use .edu/.ac)
    const knownUniversityDomains = [
      'uio.no', // University of Oslo
      'ntnu.no', // Norwegian University of Science and Technology
      'uib.no', // University of Bergen
      'uit.no', // UiT The Arctic University of Norway
      'nmbu.no', // Norwegian University of Life Sciences
      'uis.no', // University of Stavanger
      'oslomet.no', // Oslo Metropolitan University
      'khio.no', // Oslo National Academy of the Arts
      'nmh.no', // Norwegian Academy of Music
      'ku.dk', // University of Copenhagen
      'dtu.dk', // Technical University of Denmark
      'au.dk', // Aarhus University
      'cbs.dk', // Copenhagen Business School
      'su.se', // Stockholm University
      'kth.se', // KTH Royal Institute of Technology
      'gu.se', // University of Gothenburg
      'lu.se', // Lund University
      'uu.se', // Uppsala University
      'liu.se', // Linköping University
      'helsinki.fi', // University of Helsinki
      'aalto.fi', // Aalto University
      'tuni.fi', // Tampere University
      'jyu.fi', // University of Jyväskylä
      'oulu.fi', // University of Oulu
      'utu.fi', // University of Turku
    ];

    // Check for exact match or subdomain match (e.g., medisin.uio.no matches uio.no)
    for (const uniDomain of knownUniversityDomains) {
      if (domain === uniDomain || domain.endsWith('.' + uniDomain)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Validates if a domain is academic using the University Domains List API
   * This is a comprehensive check against a database of 9000+ universities worldwide
   * API: http://universities.hipolabs.com
   * GitHub: https://github.com/Hipo/university-domains-list
   *
   * @param domain - The domain to check (e.g., 'mit.edu', 'stanford.edu', 'abc.edu')
   * @returns Observable<boolean> - true if domain is found in university database
   */
  public validateAcademicDomain(domain: string): Observable<boolean> {
    if (!domain || typeof domain !== 'string') {
      return of(false);
    }

    const normalizedDomain = domain.toLowerCase().trim();

    // First check local patterns (fast)
    if (this.isAcademicDomain(normalizedDomain)) {
      return of(true);
    }

    // Then check against University Domains List API (comprehensive but slower)
    const apiUrl = `http://universities.hipolabs.com/search?domain=${encodeURIComponent(normalizedDomain)}`;

    return new Observable<boolean>((observer) => {
      fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`API query failed: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          // API returns an array of universities matching the domain
          // If array is not empty, domain is academic
          const isAcademic = Array.isArray(data) && data.length > 0;
          // console.log(`Academic domain check for ${normalizedDomain}:`, isAcademic, data);
          observer.next(isAcademic);
          observer.complete();
        })
        .catch((error) => {
          // console.warn(`Academic domain validation failed for ${normalizedDomain}:`, error);
          // On error, assume not academic (conservative approach)
          observer.next(false);
          observer.complete();
        });
    }).pipe(timeout(3000)); // 3 second timeout
  }

  /**
   * Public method to check if a domain is a known personal or academic domain
   * This is used by validators to determine the correct error type
   * @param domain - The domain to check
   * @returns boolean - true if it's a personal or academic domain
   */
  public isKnownPersonalOrAcademicDomain(domain: string): boolean {
    return this.isKnownPersonalDomain(domain);
  }

  /**
   * Extracts domain from email address
   * @param email - The email address
   * @returns string - The domain part or empty string if invalid
   */
  public extractDomain(email: string): string {
    if (!email || typeof email !== 'string') {
      return '';
    }

    const emailParts = email.toLowerCase().trim().split('@');
    return emailParts.length === 2 ? emailParts[1] : '';
  }

  /**
   * Clears the MX record cache
   */
  public clearCache(): void {
    this.mxCache.clear();
  }

  /**
   * Gets cache statistics for debugging
   */
  public getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.mxCache.size,
      entries: Array.from(this.mxCache.keys()),
    };
  }
}
